import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X, Upload, Play, Pause, Type, Download, Scissors, Trash2, Sparkles, Loader2, Smile, Music2, Wand2, Check, Captions, Palette, Share2, Copy, Plus, Mic, MicOff, Cloud, LogIn } from 'lucide-react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useLang } from '../context/LangContext';
import { useProject } from '../context/ProjectContext';
import { useAuth, API } from '../context/AuthContext';
import { useEditor } from '../context/EditorContext';
import { musicTracks } from '../mock/data';
import { toast } from '../hooks/use-toast';

const STICKERS = ['🔥','✨','💯','😂','😍','🥳','😎','🤩','💖','💫','⭐','🌟','🎉','🎊','🚀','⚡','🌈','☀️','🌊','🎵','🎶','👑','💎','🍕','🍔','🌸','🦄','🐶','🐱','👀','🙌','👏','💪','🤝','🤯','🥶','🥵','😱','🤔','😴'];

const TRANSITIONS = [
  { key: 'none', labelSv: 'Ingen', labelAr: 'بدون' },
  { key: 'fade', labelSv: 'Fade', labelAr: 'تدرج' },
  { key: 'slide', labelSv: 'Slide', labelAr: 'انزلاق' },
  { key: 'zoom', labelSv: 'Zoom', labelAr: 'تكبير' }
];

const ANIMS = [
  { key: 'none', labelSv: 'Ingen', labelAr: 'بدون' },
  { key: 'pulse', labelSv: 'Pulsera', labelAr: 'نبض' },
  { key: 'spin', labelSv: 'Rotera', labelAr: 'دوران' },
  { key: 'bounce', labelSv: 'Bouncy', labelAr: 'ارتداد' },
  { key: 'wobble', labelSv: 'Wobble', labelAr: 'اهتزاز' }
];

const FILTERS = [
  { key: 'none', labelSv: 'Ingen', labelAr: 'بدون', css: '', pro: false },
  { key: 'vintage', labelSv: 'Vintage', labelAr: 'كلاسيكي', css: 'sepia(0.6) contrast(1.05) saturate(1.1) brightness(1.02)', pro: false },
  { key: 'cinematic', labelSv: 'Cinematic', labelAr: 'سينمائي', css: 'contrast(1.25) saturate(1.15) brightness(0.95)', pro: false },
  { key: 'cold', labelSv: 'Kall', labelAr: 'بارد', css: 'hue-rotate(200deg) saturate(1.2) brightness(1.02)', pro: false },
  { key: 'warm', labelSv: 'Varm', labelAr: 'دافئ', css: 'hue-rotate(-15deg) saturate(1.35) brightness(1.05)', pro: false },
  { key: 'bw', labelSv: 'Svartvitt', labelAr: 'أبيض وأسود', css: 'grayscale(1) contrast(1.1)', pro: false },
  { key: 'dream', labelSv: 'Drömsk', labelAr: 'حالم', css: 'saturate(1.4) brightness(1.08) contrast(0.95) blur(0.3px)', pro: false },
  { key: 'vintagePlus', labelSv: 'Vintage+', labelAr: 'كلاسيكي+', css: 'sepia(0.85) contrast(1.15) saturate(0.85) brightness(0.95) hue-rotate(-10deg)' },
  { key: 'cinemaGrade', labelSv: 'Cinema Grade', labelAr: 'سينما احترافي', css: 'contrast(1.4) saturate(1.3) brightness(0.92) hue-rotate(-5deg)' },
  { key: 'noir', labelSv: 'Noir', labelAr: 'نوار', css: 'grayscale(1) contrast(1.5) brightness(0.85)' }
];

const VideoEditor = ({ open, onClose }) => {
  const { lang } = useLang();
  const { tracks: userTracks } = useProject();
  const { user, login } = useAuth();
  const { consumePreload } = useEditor();
  const t = {
    sv: { title: 'Webbredigerare', upload: 'Ladda upp video', dragHere: 'Dra & släpp en video här eller', browse: 'bläddra bland filer', formats: 'MP4, WebM, MOV upp till 200 MB', trim: 'Trimma', text: 'Text', stickers: 'Stickers', music: 'Musik', transition: 'Övergång', captions: 'Undertexter', filters: 'Filter', share: 'Dela', projects: 'Projekt', color: 'Färg', size: 'Storlek', position: 'Position', addText: 'Lägg till text', textPlaceholder: 'Skriv din text...', export: 'Exportera video', exporting: 'Exporterar...', clear: 'Rensa', top: 'Topp', center: 'Mitten', bottom: 'Botten', start: 'Start', end: 'Slut', done: 'Klart!', downloadReady: 'Din video är exporterad', pickTrack: 'Välj spår för videon', noTracks: 'Lägg till spår från Musikbiblioteket för att välja här', original: 'Originalljud', drag: 'Dra klistermärkena på förhandsvisningen', pickTransition: 'Övergångstyp', transitionInfo: 'Övergången spelas första sekunden av klippet', animation: 'Animation', pickAnim: 'Väljanimation för nya klistermärken', startAI: 'Skapa undertexter', stopAI: 'Stoppa transkribering', aiHint: 'Tips: höj ljudvolymen på högtalarna och ge mikrofontillstånd så att Web Speech API kan lyssna på videoljudet.', aiUnsupported: 'Din webbläsare stöder inte Web Speech API. Prova Chrome.', addManual: 'Lägg till manuellt', capPlaceholder: 'Text för denna tid...', addAtTime: 'Lägg till på', clearCaps: 'Rensa alla', intensity: 'Intensitet', chooseFilter: 'Välj filter', shareLink: 'Delnings-länk', shareInfo: 'Länken innehåller inte videofilen, bara redigeringsinställningar. Vänner kan öppna sin egen video och få dina inställningar.', copyLink: 'Kopiera länk', linkCopied: 'Länk kopierad!', fadeIn: 'Fade in', fadeOut: 'Fade out', sec: 's', musicFade: 'Ljud fade', fadeInfo: 'Väljer hur mjukt musiken tonar in och ut', saveProject: 'Spara projekt', projectNamePh: 'Ge projektet ett namn...', savedProjects: 'Sparade projekt', noProjects: 'Inga sparade projekt ännu.', loadProj: 'Ladda', deleteProj: 'Ta bort', stickerTime: 'Sticker-tider', selectSticker: 'Klicka på en sticker för att ändra dess visningstid' },
    ar: { title: 'محرر الويب', upload: 'تحميل الفيديو', dragHere: 'اسحب وأفلت فيديو هنا أو', browse: 'استعرض الملفات', formats: 'MP4 و WebM و MOV حتى 200 ميجابايت', trim: 'قص', text: 'نص', stickers: 'ملصقات', music: 'موسيقى', transition: 'انتقال', captions: 'ترجمة', filters: 'مرشحات', share: 'مشاركة', projects: 'مشاريع', color: 'اللون', size: 'الحجم', position: 'الموضع', addText: 'أضف نصاً', textPlaceholder: 'اكتب نصك...', export: 'تصدير الفيديو', exporting: 'جارٍ التصدير...', clear: 'مسح', top: 'أعلى', center: 'وسط', bottom: 'أسفل', start: 'البداية', end: 'النهاية', done: 'تم!', downloadReady: 'تم تصدير الفيديو', pickTrack: 'اختر مقطعاً', noTracks: 'أضف مقاطع من مكتبة الموسيقى', original: 'الصوت الأصلي', drag: 'اسحب الملصقات على المعاينة', pickTransition: 'نوع الانتقال', transitionInfo: 'يتم تشغيل الانتقال في الثانية الأولى', animation: 'الحركة', pickAnim: 'اختر حركة للملصقات الجديدة', startAI: 'إنشاء الترجمة', stopAI: 'إيقاف النسخ', aiHint: 'ملاحظة: ارفع الصوت وامنح إذن الميكروفون.', aiUnsupported: 'المتصفح لا يدعم Web Speech API. جرّب Chrome.', addManual: 'أضف يدوياً', capPlaceholder: 'نص لهذا الوقت...', addAtTime: 'أضف عند', clearCaps: 'امسح الكل', intensity: 'الشدة', chooseFilter: 'اختر مرشحاً', shareLink: 'رابط المشاركة', shareInfo: 'الرابط لا يحتوي على ملف الفيديو، فقط الإعدادات.', copyLink: 'انسخ الرابط', linkCopied: 'تم النسخ!', fadeIn: 'تلاشي داخل', fadeOut: 'تلاشي خارج', sec: 'ث', musicFade: 'تلاشي الصوت', fadeInfo: 'اختر مدى نعومة تلاشي الموسيقى', saveProject: 'حفظ المشروع', projectNamePh: 'اختر اسماً للمشروع...', savedProjects: 'مشاريع محفوظة', noProjects: 'لا توجد مشاريع محفوظة بعد.', loadProj: 'تحميل', deleteProj: 'حذف', stickerTime: 'أوقات الملصقات', selectSticker: 'انقر على ملصق لتعديل وقت ظهوره' }
  }[lang];

  const [videoUrl, setVideoUrl] = useState(null);
  const [videoName, setVideoName] = useState('');
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd, setTrimEnd] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [text, setText] = useState('');
  const [textColor, setTextColor] = useState('#ffffff');
  const [textSize, setTextSize] = useState(48);
  const [textPos, setTextPos] = useState('bottom');
  const [tab, setTab] = useState('trim');
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [stickers, setStickers] = useState([]);
  const [stickerAnim, setStickerAnim] = useState('pulse');
  const [dragId, setDragId] = useState(null);
  const [trackId, setTrackId] = useState(null);
  const [transition, setTransition] = useState('none');
  const [filterKey, setFilterKey] = useState('none');
  const [filterIntensity, setFilterIntensity] = useState(70);
  const [captions, setCaptions] = useState([]); // {t, text}
  const [manualCap, setManualCap] = useState('');
  const [aiListening, setAiListening] = useState(false);
  const [captionLang, setCaptionLang] = useState(lang === 'sv' ? 'sv-SE' : 'ar-SA');
  const [shareUrl, setShareUrl] = useState('');
  const [fadeIn, setFadeIn] = useState(0);
  const [fadeOut, setFadeOut] = useState(0);
  const [savedProjects, setSavedProjects] = useState(() => {
    try { return JSON.parse(localStorage.getItem('capcute_projects') || '[]'); } catch { return []; }
  });
  const [cloudProjects, setCloudProjects] = useState([]);
  const [projectName, setProjectName] = useState('');
  const [cloudUrl, setCloudUrl] = useState('');
  const [tagsInput, setTagsInput] = useState('');

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const fileInputRef = useRef(null);
  const audioElRef = useRef(null);
  const recognitionRef = useRef(null);
  const dragOffsetRef = useRef({ x: 0, y: 0 });

  const availableTracks = useMemo(() => {
    if (userTracks.length === 0) return [];
    return userTracks.map(ut => musicTracks.find(m => m.id === ut.id)).filter(Boolean);
  }, [userTracks]);

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = 'hidden';
    // Consume preloaded video from EditorContext (for "Reopen from My Videos")
    const p = consumePreload && consumePreload();
    if (p && p.videoUrl) {
      setVideoUrl(p.videoUrl);
      setVideoName(p.videoName || '');
      if (p.settings) {
        const s = p.settings;
        if (s.text !== undefined) setText(s.text || '');
        if (s.textColor) setTextColor(s.textColor);
        if (s.textSize) setTextSize(s.textSize);
        if (s.textPos) setTextPos(s.textPos);
        if (s.stickers) setStickers(s.stickers);
        if (s.stickerAnim) setStickerAnim(s.stickerAnim);
        if (s.transition) setTransition(s.transition);
        if (s.filterKey) setFilterKey(s.filterKey);
        if (s.filterIntensity !== undefined) setFilterIntensity(s.filterIntensity);
        if (s.captions) setCaptions(s.captions);
        if (s.trackId) setTrackId(s.trackId);
        if (s.fadeIn !== undefined) setFadeIn(s.fadeIn);
        if (s.fadeOut !== undefined) setFadeOut(s.fadeOut);
      }
      toast({ title: lang === 'sv' ? 'Video laddad från molnet' : 'تم تحميل الفيديو من السحابة' });
    }
    // Read share hash
    try {
      const hash = window.location.hash;
      if (hash.startsWith('#p=')) {
        const decoded = JSON.parse(atob(decodeURIComponent(hash.slice(3))));
        if (decoded.text !== undefined) setText(decoded.text || '');
        if (decoded.textColor) setTextColor(decoded.textColor);
        if (decoded.textSize) setTextSize(decoded.textSize);
        if (decoded.textPos) setTextPos(decoded.textPos);
        if (decoded.stickers) setStickers(decoded.stickers);
        if (decoded.stickerAnim) setStickerAnim(decoded.stickerAnim);
        if (decoded.transition) setTransition(decoded.transition);
        if (decoded.filterKey) setFilterKey(decoded.filterKey);
        if (decoded.filterIntensity !== undefined) setFilterIntensity(decoded.filterIntensity);
        if (decoded.captions) setCaptions(decoded.captions);
        toast({ title: lang === 'sv' ? 'Delade inställningar laddades' : 'تم تحميل الإعدادات المشتركة' });
      }
    } catch (_) {}
    return () => { document.body.style.overflow = ''; };
  }, [open, lang]);

  useEffect(() => {
    if (audioElRef.current) { audioElRef.current.pause(); audioElRef.current = null; }
    if (!trackId || !playing) return;
    const trk = musicTracks.find(m => m.id === trackId);
    if (!trk) return;
    const a = new Audio(trk.url);
    a.loop = true; a.volume = 0.7;
    a.play().catch(() => {});
    audioElRef.current = a;
    return () => { a.pause(); };
  }, [trackId, playing]);

  useEffect(() => {
    if (videoRef.current) videoRef.current.muted = !!trackId;
  }, [trackId, videoUrl]);

  // Draw loop
  useEffect(() => {
    if (!videoUrl) return;
    const draw = () => {
      const v = videoRef.current;
      const c = canvasRef.current;
      if (!v || !c || v.readyState < 2) { rafRef.current = requestAnimationFrame(draw); return; }
      const ctx = c.getContext('2d');
      c.width = v.videoWidth || 1280;
      c.height = v.videoHeight || 720;

      const elapsed = Math.max(0, v.currentTime - trimStart);
      const trans = elapsed < 1 ? applyTransition(ctx, c, transition, elapsed) : null;

      // Apply CSS filter for video only (canvas ctx.filter)
      const f = FILTERS.find(x => x.key === filterKey);
      if (f && f.css) {
        const p = filterIntensity / 100;
        // Scale intensity by adjusting alpha with a layered composite
        ctx.filter = f.css;
      } else {
        ctx.filter = 'none';
      }
      ctx.drawImage(v, 0, 0, c.width, c.height);
      // Blend a partial layer to reduce intensity: draw normal on top with (1-intensity)
      if (f && f.css && filterIntensity < 100) {
        ctx.filter = 'none';
        ctx.globalAlpha = 1 - filterIntensity / 100;
        ctx.drawImage(v, 0, 0, c.width, c.height);
        ctx.globalAlpha = 1;
      }
      ctx.filter = 'none';
      if (trans) ctx.restore();

      // Text overlay
      if (text) drawText(ctx, c.width, c.height);

      // Captions
      const activeCap = getActiveCaption(v.currentTime);
      if (activeCap) drawCaption(ctx, c.width, c.height, activeCap.text);

      // Stickers with animations - only show within their time range
      stickers.forEach(s => {
        const st = s.startTime ?? trimStart;
        const en = s.endTime ?? trimEnd;
        if (v.currentTime >= st && v.currentTime <= en) {
          drawSticker(ctx, s, c.width, c.height, performance.now());
        }
      });

      rafRef.current = requestAnimationFrame(draw);
    };
    rafRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(rafRef.current);
  }, [videoUrl, text, textColor, textSize, textPos, stickers, transition, trimStart, filterKey, filterIntensity, captions]);

  const applyTransition = (ctx, c, kind, elapsed) => {
    if (kind === 'none') return null;
    const p = Math.min(1, elapsed);
    ctx.save();
    if (kind === 'fade') {
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, c.width, c.height);
      ctx.globalAlpha = p;
    } else if (kind === 'slide') {
      const off = (1 - p) * c.width;
      ctx.translate(off, 0);
    } else if (kind === 'zoom') {
      const scale = 0.6 + 0.4 * p;
      ctx.translate(c.width / 2, c.height / 2);
      ctx.scale(scale, scale);
      ctx.translate(-c.width / 2, -c.height / 2);
      ctx.globalAlpha = p;
    }
    return true;
  };

  const drawText = (ctx, w, h) => {
    ctx.save();
    ctx.font = `900 ${textSize}px Inter, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const x = w / 2;
    const y = textPos === 'top' ? textSize + 40 : textPos === 'center' ? h / 2 : h - textSize - 40;
    const metrics = ctx.measureText(text);
    const padX = 24, padY = 12;
    const bw = metrics.width + padX * 2;
    const bh = textSize + padY * 2;
    const rx = x - bw / 2, ry = y - bh / 2, r = bh / 2;
    ctx.fillStyle = 'rgba(0,0,0,0.55)';
    ctx.beginPath();
    ctx.moveTo(rx + r, ry);
    ctx.arcTo(rx + bw, ry, rx + bw, ry + bh, r);
    ctx.arcTo(rx + bw, ry + bh, rx, ry + bh, r);
    ctx.arcTo(rx, ry + bh, rx, ry, r);
    ctx.arcTo(rx, ry, rx + bw, ry, r);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = textColor;
    ctx.fillText(text, x, y);
    ctx.restore();
  };

  const drawCaption = (ctx, w, h, txt) => {
    ctx.save();
    const size = Math.max(28, Math.floor(w / 32));
    ctx.font = `800 ${size}px Inter, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const y = h - size * 1.6;
    // wrap words if too long
    const maxWidth = w * 0.85;
    const words = txt.split(' ');
    const lines = [];
    let line = '';
    words.forEach(word => {
      const test = line ? line + ' ' + word : word;
      if (ctx.measureText(test).width > maxWidth) { lines.push(line); line = word; }
      else line = test;
    });
    if (line) lines.push(line);
    const lh = size * 1.25;
    lines.forEach((ln, i) => {
      const yy = y - (lines.length - 1 - i) * lh;
      const m = ctx.measureText(ln);
      const padX = 20, padY = 8;
      const bw = m.width + padX * 2, bh = size + padY * 2;
      const rx = w / 2 - bw / 2, ry = yy - bh / 2, r = 10;
      ctx.fillStyle = 'rgba(0,0,0,0.75)';
      ctx.beginPath();
      ctx.moveTo(rx + r, ry);
      ctx.arcTo(rx + bw, ry, rx + bw, ry + bh, r);
      ctx.arcTo(rx + bw, ry + bh, rx, ry + bh, r);
      ctx.arcTo(rx, ry + bh, rx, ry, r);
      ctx.arcTo(rx, ry, rx + bw, ry, r);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = '#ffffff';
      ctx.fillText(ln, w / 2, yy);
    });
    ctx.restore();
  };

  const getActiveCaption = (time) => {
    // Show caption whose start time is <= currentTime, until next one or +3s
    for (let i = captions.length - 1; i >= 0; i--) {
      const c = captions[i];
      const next = captions[i + 1];
      const end = next ? next.t : c.t + 3;
      if (time >= c.t && time < end) return c;
    }
    return null;
  };

  const drawSticker = (ctx, s, w, h, tMs) => {
    ctx.save();
    const cx = s.x * w, cy = s.y * h;
    const anim = s.anim || 'none';
    const time = tMs / 1000;
    let scale = 1, rot = 0, dy = 0;
    if (anim === 'pulse') scale = 1 + 0.15 * Math.sin(time * 4);
    else if (anim === 'spin') rot = time * 1.5;
    else if (anim === 'bounce') dy = Math.abs(Math.sin(time * 3)) * -30;
    else if (anim === 'wobble') rot = Math.sin(time * 5) * 0.25;
    ctx.translate(cx, cy + dy);
    ctx.rotate(rot);
    ctx.scale(scale, scale);
    ctx.font = `${s.size}px "Segoe UI Emoji","Apple Color Emoji","Noto Color Emoji",sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(s.emoji, 0, 0);
    ctx.restore();
  };

  const addSticker = (emoji) => {
    const id = `s${Date.now()}${Math.random().toString(36).slice(2, 5)}`;
    setStickers(prev => [...prev, { id, emoji, x: 0.5, y: 0.5, size: 120, anim: stickerAnim, startTime: trimStart, endTime: trimEnd }]);
  };
  const removeSticker = (id) => setStickers(prev => prev.filter(s => s.id !== id));

  const onCanvasMouseDown = (e) => {
    const c = canvasRef.current;
    if (!c || stickers.length === 0) return;
    const rect = c.getBoundingClientRect();
    const scaleX = c.width / rect.width;
    const scaleY = c.height / rect.height;
    const px = (e.clientX - rect.left) * scaleX;
    const py = (e.clientY - rect.top) * scaleY;
    for (let i = stickers.length - 1; i >= 0; i--) {
      const s = stickers[i];
      const sx = s.x * c.width, sy = s.y * c.height, half = s.size * 0.6;
      if (px >= sx - half && px <= sx + half && py >= sy - half && py <= sy + half) {
        setDragId(s.id);
        dragOffsetRef.current = { x: px - sx, y: py - sy };
        return;
      }
    }
  };
  const onCanvasMouseMove = (e) => {
    if (!dragId) return;
    const c = canvasRef.current;
    if (!c) return;
    const rect = c.getBoundingClientRect();
    const scaleX = c.width / rect.width;
    const scaleY = c.height / rect.height;
    const px = (e.clientX - rect.left) * scaleX - dragOffsetRef.current.x;
    const py = (e.clientY - rect.top) * scaleY - dragOffsetRef.current.y;
    setStickers(prev => prev.map(s => s.id === dragId ? { ...s, x: Math.max(0.02, Math.min(0.98, px / c.width)), y: Math.max(0.02, Math.min(0.98, py / c.height)) } : s));
  };
  const onCanvasMouseUp = () => setDragId(null);

  const handleFile = (file) => {
    if (!file || !file.type.startsWith('video/')) {
      toast({ title: lang === 'sv' ? 'Ogiltig fil' : 'ملف غير صالح' });
      return;
    }
    const url = URL.createObjectURL(file);
    setVideoUrl(url); setVideoName(file.name);
  };
  const onLoadedMeta = () => {
    const v = videoRef.current;
    if (!v) return;
    setDuration(v.duration); setTrimStart(0); setTrimEnd(v.duration); setCurrentTime(0);
  };
  const onTimeUpdate = () => {
    const v = videoRef.current;
    if (!v) return;
    setCurrentTime(v.currentTime);
    if (v.currentTime >= trimEnd) { v.pause(); setPlaying(false); v.currentTime = trimStart; }
    if (v.currentTime < trimStart) v.currentTime = trimStart;
  };
  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (playing) { v.pause(); setPlaying(false); }
    else {
      if (v.currentTime < trimStart || v.currentTime >= trimEnd) v.currentTime = trimStart;
      v.play(); setPlaying(true);
    }
  };

  const fmt = (s) => {
    if (!isFinite(s)) return '0:00';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  // ---------- AI Captions via Web Speech API ----------
  const speechSupported = typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition);

  const startAI = () => {
    if (!speechSupported) { toast({ title: t.aiUnsupported }); return; }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SR();
    recognition.lang = captionLang;
    recognition.continuous = true;
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    let lastText = '';
    let lastTime = 0;
    const punctuate = (raw) => {
      let s = raw.trim();
      if (!s) return s;
      // Capitalize first letter
      s = s[0].toUpperCase() + s.slice(1);
      // Add period if no ending punctuation
      if (!/[.!?…،؟]$/.test(s)) s += captionLang.startsWith('ar') ? '.' : '.';
      return s;
    };
    recognition.onresult = (event) => {
      const v = videoRef.current;
      const time = v ? v.currentTime : currentTime;
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const r = event.results[i];
        if (r.isFinal) {
          const txt = punctuate(r[0].transcript);
          const gap = time - lastTime;
          if (txt && txt !== lastText) {
            // If gap < 0.4s, merge with previous caption instead of adding new one
            if (gap < 0.4 && lastText) {
              setCaptions(prev => {
                if (prev.length === 0) return prev;
                const copy = [...prev];
                copy[copy.length - 1] = { ...copy[copy.length - 1], text: `${copy[copy.length - 1].text.replace(/[.!?]$/, '')} ${txt}` };
                return copy;
              });
            } else {
              setCaptions(prev => [...prev, { t: Math.max(0, time - 0.3), text: txt }].sort((a, b) => a.t - b.t));
            }
            lastText = txt;
            lastTime = time;
          }
        }
      }
    };
    recognition.onerror = (e) => {
      if (e.error === 'not-allowed') toast({ title: lang === 'sv' ? 'Ge mikrofontillstånd' : 'يرجى منح إذن الميكروفون' });
      setAiListening(false);
    };
    recognition.onend = () => { if (aiListening) { try { recognition.start(); } catch(_){} } };
    try {
      recognition.start();
      recognitionRef.current = recognition;
      setAiListening(true);
      // Also start playback from trimStart
      const v = videoRef.current;
      if (v) { v.currentTime = trimStart; v.muted = false; v.play(); setPlaying(true); }
      toast({ title: lang === 'sv' ? 'Lyssnar...' : 'يستمع...', description: t.aiHint });
    } catch(e) { toast({ title: String(e.message || e) }); }
  };
  const stopAI = () => {
    setAiListening(false);
    try { recognitionRef.current?.stop(); } catch(_){}
    recognitionRef.current = null;
    const v = videoRef.current;
    if (v) { v.pause(); setPlaying(false); }
  };
  const addManualCaption = () => {
    if (!manualCap.trim()) return;
    setCaptions(prev => [...prev, { t: currentTime, text: manualCap.trim() }].sort((a, b) => a.t - b.t));
    setManualCap('');
  };
  const removeCaption = (idx) => setCaptions(prev => prev.filter((_, i) => i !== idx));

  // ---------- Share ----------
  const buildShareLink = () => {
    const payload = {
      text, textColor, textSize, textPos,
      stickers, stickerAnim,
      transition, filterKey, filterIntensity,
      captions
    };
    const hash = encodeURIComponent(btoa(unescape(encodeURIComponent(JSON.stringify(payload)))));
    const url = `${window.location.origin}${window.location.pathname}#p=${hash}`;
    setShareUrl(url);
    return url;
  };
  const copyShareLink = async () => {
    const url = shareUrl || buildShareLink();
    try {
      await navigator.clipboard.writeText(url);
      toast({ title: t.linkCopied });
    } catch (_) {
      toast({ title: url });
    }
  };

  // ---------- Bookmarked Projects ----------
  const currentSettings = () => ({
    text, textColor, textSize, textPos,
    stickers, stickerAnim,
    transition, filterKey, filterIntensity,
    captions, trackId, fadeIn, fadeOut,
    trimStart, trimEnd
  });

  // --- Autosave draft every 60s (settings only, not video file) ---
  const [autoSavedAt, setAutoSavedAt] = useState(null);
  useEffect(() => {
    if (!open || !videoUrl) return;
    const doAutoSave = () => {
      try {
        const draft = { settings: currentSettings(), videoName, savedAt: new Date().toISOString() };
        localStorage.setItem('capcute_autosave', JSON.stringify(draft));
        setAutoSavedAt(draft.savedAt);
      } catch (_) {}
    };
    doAutoSave();
    const id = setInterval(doAutoSave, 60000);
    return () => clearInterval(id);
  }, [open, videoUrl, text, textColor, textSize, textPos, stickers, stickerAnim, transition, filterKey, filterIntensity, captions, trackId, fadeIn, fadeOut, trimStart, trimEnd, videoName]);

  const restoreAutoSave = () => {
    try {
      const raw = localStorage.getItem('capcute_autosave');
      if (!raw) return;
      const d = JSON.parse(raw);
      const s = d.settings || {};
      if (s.text !== undefined) setText(s.text || '');
      if (s.textColor) setTextColor(s.textColor);
      if (s.textSize) setTextSize(s.textSize);
      if (s.textPos) setTextPos(s.textPos);
      if (s.stickers) setStickers(s.stickers);
      if (s.stickerAnim) setStickerAnim(s.stickerAnim);
      if (s.transition) setTransition(s.transition);
      if (s.filterKey) setFilterKey(s.filterKey);
      if (s.filterIntensity !== undefined) setFilterIntensity(s.filterIntensity);
      if (s.captions) setCaptions(s.captions);
      if (s.trackId) setTrackId(s.trackId);
      if (s.fadeIn !== undefined) setFadeIn(s.fadeIn);
      if (s.fadeOut !== undefined) setFadeOut(s.fadeOut);
      if (s.trimStart !== undefined) setTrimStart(s.trimStart);
      if (s.trimEnd !== undefined) setTrimEnd(s.trimEnd);
      toast({ title: lang === 'sv' ? 'Utkast återställt' : 'تم استعادة المسودة', description: d.videoName || '' });
    } catch (_) {}
  };

  const saveProject = () => {
    const name = (projectName || videoName || 'Projekt').replace(/\.[^.]+$/, '');
    let thumb = '';
    try {
      const c = canvasRef.current;
      if (c && c.width) {
        const off = document.createElement('canvas');
        off.width = 240; off.height = 135;
        const octx = off.getContext('2d');
        octx.drawImage(c, 0, 0, 240, 135);
        thumb = off.toDataURL('image/jpeg', 0.6);
      }
    } catch (_) {}
    const proj = {
      id: `p${Date.now()}`,
      name,
      videoName,
      thumb,
      settings: currentSettings(),
      savedAt: new Date().toISOString()
    };
    const next = [proj, ...savedProjects].slice(0, 24);
    setSavedProjects(next);
    localStorage.setItem('capcute_projects', JSON.stringify(next));
    setProjectName('');
    toast({ title: lang === 'sv' ? 'Projekt sparat lokalt!' : 'تم الحفظ محلياً!', description: name });
  };

  const saveProjectCloud = async () => {
    if (!user) { login(); return; }
    const name = (projectName || videoName || 'Projekt').replace(/\.[^.]+$/, '');
    let thumbDataUrl = null;
    try {
      const c = canvasRef.current;
      if (c && c.width) {
        const off = document.createElement('canvas');
        off.width = 480; off.height = 270;
        off.getContext('2d').drawImage(c, 0, 0, 480, 270);
        thumbDataUrl = off.toDataURL('image/jpeg', 0.75);
      }
    } catch (_) {}
    try {
      const res = await axios.post(`${API}/projects`, {
        name, thumb_data_url: thumbDataUrl, settings: currentSettings(), video_name: videoName
      }, { withCredentials: true });
      setCloudProjects(prev => [res.data, ...prev]);
      setProjectName('');
      toast({ title: lang === 'sv' ? 'Sparat i molnet!' : 'تم الحفظ في السحابة!', description: name });
    } catch (e) {
      toast({ title: 'Sparning misslyckades', description: String(e.message || e) });
    }
  };

  const fetchCloudProjects = async () => {
    if (!user) return;
    try {
      const res = await axios.get(`${API}/projects`, { withCredentials: true });
      setCloudProjects(res.data.projects || []);
    } catch (_) {}
  };

  useEffect(() => {
    if (open && user) fetchCloudProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user]);

  const loadCloudProject = (proj) => {
    const s = proj.settings || {};
    if (s.text !== undefined) setText(s.text || '');
    if (s.textColor) setTextColor(s.textColor);
    if (s.textSize) setTextSize(s.textSize);
    if (s.textPos) setTextPos(s.textPos);
    if (s.stickers) setStickers(s.stickers);
    if (s.stickerAnim) setStickerAnim(s.stickerAnim);
    if (s.transition) setTransition(s.transition);
    if (s.filterKey) setFilterKey(s.filterKey);
    if (s.filterIntensity !== undefined) setFilterIntensity(s.filterIntensity);
    if (s.captions) setCaptions(s.captions);
    if (s.trackId) setTrackId(s.trackId);
    if (s.fadeIn !== undefined) setFadeIn(s.fadeIn);
    if (s.fadeOut !== undefined) setFadeOut(s.fadeOut);
    toast({ title: lang === 'sv' ? 'Molnprojekt laddat!' : 'تم تحميل المشروع السحابي!', description: proj.name });
  };

  const deleteCloudProject = async (id) => {
    try {
      await axios.delete(`${API}/projects/${id}`, { withCredentials: true });
      setCloudProjects(prev => prev.filter(p => p.id !== id));
    } catch (_) {}
  };

  const loadProject = (proj) => {
    const s = proj.settings || {};
    if (s.text !== undefined) setText(s.text || '');
    if (s.textColor) setTextColor(s.textColor);
    if (s.textSize) setTextSize(s.textSize);
    if (s.textPos) setTextPos(s.textPos);
    if (s.stickers) setStickers(s.stickers);
    if (s.stickerAnim) setStickerAnim(s.stickerAnim);
    if (s.transition) setTransition(s.transition);
    if (s.filterKey) setFilterKey(s.filterKey);
    if (s.filterIntensity !== undefined) setFilterIntensity(s.filterIntensity);
    if (s.captions) setCaptions(s.captions);
    if (s.trackId) setTrackId(s.trackId);
    if (s.fadeIn !== undefined) setFadeIn(s.fadeIn);
    if (s.fadeOut !== undefined) setFadeOut(s.fadeOut);
    toast({ title: lang === 'sv' ? 'Projekt laddat!' : 'تم تحميل المشروع!', description: proj.name });
  };

  const deleteProject = (id) => {
    const next = savedProjects.filter(p => p.id !== id);
    setSavedProjects(next);
    localStorage.setItem('capcute_projects', JSON.stringify(next));
  };

  // ---------- Export ----------
  const exportVideo = async () => {
    const v = videoRef.current;
    const c = canvasRef.current;
    if (!v || !c) return;
    setExporting(true);
    setExportProgress(0);
    let audioContext = null, musicAudioEl = null;
    try {
      const stream = c.captureStream(30);
      const combined = new MediaStream();
      stream.getVideoTracks().forEach(tr => combined.addTrack(tr));
      if (trackId) {
        const trk = musicTracks.find(m => m.id === trackId);
        audioContext = new (window.AudioContext || window.webkitAudioContext)();
        musicAudioEl = new Audio(trk.url);
        musicAudioEl.crossOrigin = 'anonymous';
        musicAudioEl.loop = true;
        const src = audioContext.createMediaElementSource(musicAudioEl);
        const gainNode = audioContext.createGain();
        const dest = audioContext.createMediaStreamDestination();
        src.connect(gainNode);
        gainNode.connect(dest);
        // Schedule fade in / fade out
        const now = audioContext.currentTime;
        const totalLen = Math.max(0.1, trimEnd - trimStart);
        const fi = Math.min(fadeIn, totalLen / 2);
        const fo = Math.min(fadeOut, totalLen / 2);
        gainNode.gain.setValueAtTime(fi > 0 ? 0.0001 : 1, now);
        if (fi > 0) gainNode.gain.linearRampToValueAtTime(1, now + fi);
        if (fo > 0) {
          gainNode.gain.setValueAtTime(1, now + totalLen - fo);
          gainNode.gain.linearRampToValueAtTime(0.0001, now + totalLen);
        }
        dest.stream.getAudioTracks().forEach(tr => combined.addTrack(tr));
      } else if (v.captureStream) {
        v.muted = false;
        const vs = v.captureStream();
        vs.getAudioTracks().forEach(tr => combined.addTrack(tr));
      }
      const mime = MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus') ? 'video/webm;codecs=vp9,opus' : 'video/webm';
      const rec = new MediaRecorder(combined, { mimeType: mime, videoBitsPerSecond: 4_000_000 });
      const chunks = [];
      rec.ondataavailable = (e) => e.data.size > 0 && chunks.push(e.data);
      const stopPromise = new Promise(res => { rec.onstop = res; });
      v.currentTime = trimStart;
      await new Promise(r => { v.onseeked = r; });
      if (musicAudioEl) { try { await musicAudioEl.play(); } catch(_){} }
      v.play();
      rec.start(100);
      const totalLen = Math.max(0.1, trimEnd - trimStart);
      const tick = setInterval(() => {
        const done = Math.min(100, ((v.currentTime - trimStart) / totalLen) * 100);
        setExportProgress(done);
      }, 100);
      const check = () => {
        if (v.currentTime >= trimEnd || v.ended) {
          clearInterval(tick); v.pause();
          if (musicAudioEl) musicAudioEl.pause();
          rec.stop();
        } else requestAnimationFrame(check);
      };
      requestAnimationFrame(check);
      await stopPromise;
      const blob = new Blob(chunks, { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `capcute-${Date.now()}.webm`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      setExportProgress(100);

      // Auto-upload to cloud (works for both anon and logged-in users)
      try {
        const form = new FormData();
        const filename = (projectName || videoName || 'CapCute video').replace(/\.[^.]+$/, '') + '.webm';
        form.append('file', blob, filename);
        form.append('title', filename);
        form.append('duration', String(trimEnd - trimStart));
        form.append('settings', JSON.stringify(currentSettings()));
        form.append('tags', tagsInput);
        const res = await axios.post(`${API}/videos/upload`, form, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
        const uploaded = res.data;
        // Also upload thumbnail
        try {
          const off = document.createElement('canvas');
          off.width = 640; off.height = 360;
          off.getContext('2d').drawImage(c, 0, 0, 640, 360);
          const thumbBlob = await new Promise(r => off.toBlob(r, 'image/jpeg', 0.7));
          if (thumbBlob) {
            const tf = new FormData();
            tf.append('file', thumbBlob, 'thumb.jpg');
            await axios.post(`${API}/videos/${uploaded.id}/thumbnail`, tf, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
          }
        } catch (_) {}
        const shareUrl = `${window.location.origin}/v/${uploaded.id}`;
        setCloudUrl(shareUrl);
        toast({ title: t.done, description: lang === 'sv' ? 'Uppladdad till molnet' : 'تم الرفع إلى السحابة' });
      } catch (upErr) {
        toast({ title: t.done, description: t.downloadReady });
      }
    } catch (e) {
      toast({ title: 'Export failed', description: String(e.message || e) });
    } finally {
      if (audioContext) audioContext.close().catch(() => {});
      setExporting(false);
      setTimeout(() => setExportProgress(0), 1500);
    }
  };

  const clearVideo = () => {
    if (videoUrl) URL.revokeObjectURL(videoUrl);
    setVideoUrl(null); setVideoName(''); setDuration(0); setPlaying(false);
  };

  const onDrop = (e) => { e.preventDefault(); handleFile(e.dataTransfer.files?.[0]); };

  if (!open) return null;

  const tabs = [
    { k: 'trim', l: t.trim, I: Scissors },
    { k: 'text', l: t.text, I: Type },
    { k: 'stickers', l: t.stickers, I: Smile },
    { k: 'filters', l: t.filters, I: Palette },
    { k: 'captions', l: t.captions, I: Captions },
    { k: 'music', l: t.music, I: Music2 },
    { k: 'transition', l: t.transition, I: Wand2 },
    { k: 'projects', l: t.projects, I: Sparkles },
    { k: 'share', l: t.share, I: Share2 }
  ];

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-2 md:p-6" onClick={onClose}>
      <div className="absolute inset-0 bg-black/80 backdrop-blur-md" />
      <div className="relative bg-neutral-950 text-white rounded-3xl shadow-2xl overflow-hidden w-full max-w-6xl max-h-[95vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-cyan-400 to-fuchsia-500 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <div>
              <div className="font-black text-[16px] tracking-tight">{t.title}</div>
              {videoName && <div className="text-[11.5px] text-neutral-400 truncate max-w-[240px]">{videoName}</div>}
            </div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center" aria-label="close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {!videoUrl ? (
          <div className="flex-1 p-6 flex items-center justify-center min-h-[400px]">
            <div onDragOver={(e) => e.preventDefault()} onDrop={onDrop}
              className="w-full max-w-xl border-2 border-dashed border-white/20 hover:border-cyan-400 hover:bg-white/5 rounded-3xl p-12 text-center transition-colors cursor-pointer"
              onClick={() => fileInputRef.current?.click()}>
              <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-cyan-500 to-fuchsia-500 flex items-center justify-center mb-5">
                <Upload className="w-7 h-7 text-white" />
              </div>
              <div className="text-[18px] font-bold mb-1">{t.upload}</div>
              <div className="text-[13px] text-neutral-400">{t.dragHere} <span className="text-cyan-400 font-semibold">{t.browse}</span></div>
              <div className="text-[11.5px] text-neutral-500 mt-3">{t.formats}</div>
              <input ref={fileInputRef} type="file" accept="video/*" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} />
            </div>
          </div>
        ) : (
          <div className="flex-1 grid md:grid-cols-[minmax(0,1fr)_360px] overflow-hidden">
            <div className="p-4 md:p-6 flex flex-col min-h-0 overflow-y-auto">
              <div className="relative rounded-2xl overflow-hidden bg-black aspect-video">
                <canvas ref={canvasRef}
                  className={`w-full h-full object-contain ${stickers.length ? 'cursor-move' : ''}`}
                  onMouseDown={onCanvasMouseDown} onMouseMove={onCanvasMouseMove}
                  onMouseUp={onCanvasMouseUp} onMouseLeave={onCanvasMouseUp} />
                <video ref={videoRef} src={videoUrl} className="hidden" onLoadedMetadata={onLoadedMeta} onTimeUpdate={onTimeUpdate} onEnded={() => setPlaying(false)} crossOrigin="anonymous" />
                {exporting && (
                  <div className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center gap-3">
                    <Loader2 className="w-10 h-10 animate-spin text-cyan-400" />
                    <div className="text-[13px] font-semibold">{t.exporting} {Math.round(exportProgress)}%</div>
                    <div className="w-48 h-1.5 bg-white/10 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-cyan-400 to-fuchsia-500 transition-[width]" style={{ width: `${exportProgress}%` }} />
                    </div>
                  </div>
                )}
                {aiListening && (
                  <div className="absolute top-3 left-3 rtl:left-auto rtl:right-3 flex items-center gap-2 bg-red-500 rounded-full px-3 py-1.5 shadow-lg">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
                    </span>
                    <span className="text-[11px] font-bold uppercase tracking-wider">REC</span>
                  </div>
                )}
              </div>

              <div className="mt-4 flex items-center gap-3">
                <button onClick={togglePlay} className="w-11 h-11 rounded-full bg-white text-black flex items-center justify-center hover:scale-105 transition-transform">
                  {playing ? <Pause className="w-4 h-4 fill-black" /> : <Play className="w-4 h-4 fill-black ml-0.5" />}
                </button>
                <div className="flex-1 relative h-11 flex items-center">
                  <div className="absolute inset-x-0 h-2 bg-white/10 rounded-full" />
                  <div className="absolute h-2 bg-cyan-500/40 rounded-full" style={{ left: `${(trimStart / (duration || 1)) * 100}%`, right: `${100 - (trimEnd / (duration || 1)) * 100}%` }} />
                  <div className="absolute w-1 h-6 bg-white rounded-full -translate-x-1/2" style={{ left: `${(currentTime / (duration || 1)) * 100}%` }} />
                  {captions.map((c, i) => (
                    <div key={i} className="absolute w-1.5 h-4 bg-amber-400 rounded-full -translate-x-1/2 top-1" style={{ left: `${(c.t / (duration || 1)) * 100}%` }} title={c.text} />
                  ))}
                </div>
                <div className="text-[12px] font-semibold tabular-nums text-neutral-300">{fmt(currentTime)} / {fmt(duration)}</div>
              </div>

              <div className="mt-5">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-neutral-400 flex items-center gap-1.5"><Scissors className="w-3 h-3" /> {t.trim}</div>
                  <div className="text-[11.5px] text-neutral-400 tabular-nums">{fmt(trimStart)} → {fmt(trimEnd)}</div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <label className="block">
                    <span className="text-[10.5px] text-neutral-500 uppercase font-semibold">{t.start}</span>
                    <input type="range" min={0} max={duration} step={0.05} value={trimStart} onChange={(e) => setTrimStart(Math.min(parseFloat(e.target.value), trimEnd - 0.2))} className="w-full accent-cyan-400" />
                  </label>
                  <label className="block">
                    <span className="text-[10.5px] text-neutral-500 uppercase font-semibold">{t.end}</span>
                    <input type="range" min={0} max={duration} step={0.05} value={trimEnd} onChange={(e) => setTrimEnd(Math.max(parseFloat(e.target.value), trimStart + 0.2))} className="w-full accent-fuchsia-400" />
                  </label>
                </div>
              </div>

              {stickers.length > 0 && (
                <div className="mt-5">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-neutral-400 mb-2 flex items-center gap-1.5"><Smile className="w-3 h-3" /> {stickers.length} stickers · {t.stickerTime}</div>
                  <div className="space-y-2">
                    {stickers.map(s => (
                      <div key={s.id} className="p-2.5 rounded-xl bg-white/5 border border-white/10">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xl">{s.emoji}</span>
                          <span className="text-[10.5px] uppercase tracking-wider font-bold text-neutral-400">{s.anim}</span>
                          <span className="ml-auto rtl:ml-0 rtl:mr-auto text-[11px] font-semibold text-cyan-300 tabular-nums">{fmt(s.startTime ?? trimStart)} → {fmt(s.endTime ?? trimEnd)}</span>
                          <button onClick={() => removeSticker(s.id)} className="w-7 h-7 rounded-full hover:bg-red-500/30 text-neutral-400 hover:text-red-300 flex items-center justify-center">
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <input type="range" min={0} max={duration || 0} step={0.1} value={s.startTime ?? trimStart} onChange={(e) => {
                            const val = Math.min(parseFloat(e.target.value), (s.endTime ?? trimEnd) - 0.1);
                            setStickers(prev => prev.map(x => x.id === s.id ? { ...x, startTime: val } : x));
                          }} className="w-full accent-cyan-400" />
                          <input type="range" min={0} max={duration || 0} step={0.1} value={s.endTime ?? trimEnd} onChange={(e) => {
                            const val = Math.max(parseFloat(e.target.value), (s.startTime ?? trimStart) + 0.1);
                            setStickers(prev => prev.map(x => x.id === s.id ? { ...x, endTime: val } : x));
                          }} className="w-full accent-fuchsia-400" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <aside className="border-t md:border-t-0 md:border-l rtl:md:border-l-0 rtl:md:border-r border-white/10 bg-neutral-900 flex flex-col overflow-hidden">
              <div className="flex border-b border-white/10 overflow-x-auto flex-shrink-0" style={{ scrollbarWidth: 'thin' }}>
                {tabs.map(x => {
                  const I = x.I;
                  return (
                    <button key={x.k} onClick={() => setTab(x.k)} className={`flex-shrink-0 min-w-[70px] py-3 px-2 text-[11px] font-bold flex flex-col items-center justify-center gap-1 transition-colors border-b-2 ${tab === x.k ? 'bg-white/5 text-white border-cyan-400' : 'text-neutral-500 hover:text-white border-transparent'}`}>
                      <I className="w-3.5 h-3.5" /> {x.l}
                    </button>
                  );
                })}
              </div>
              <div className="flex-1 p-5 overflow-y-auto">
                {tab === 'trim' && (
                  <div className="space-y-4">
                    <div className="p-4 rounded-xl bg-white/5">
                      <div className="text-[11px] uppercase tracking-wider text-neutral-500 font-bold mb-2">{lang === 'sv' ? 'Klipplängd' : 'مدة المقطع'}</div>
                      <div className="text-3xl font-black tabular-nums">{fmt(trimEnd - trimStart)}</div>
                      <div className="text-[11.5px] text-neutral-500 mt-1">av {fmt(duration)}</div>
                    </div>
                  </div>
                )}
                {tab === 'text' && (
                  <div className="space-y-4">
                    <div>
                      <label className="text-[11px] uppercase tracking-wider text-neutral-500 font-bold">{t.addText}</label>
                      <Input value={text} onChange={(e) => setText(e.target.value)} placeholder={t.textPlaceholder} className="mt-1.5 bg-white/5 border-white/10 text-white placeholder:text-neutral-500 h-11 rounded-xl focus-visible:ring-cyan-400 focus-visible:ring-offset-0" />
                    </div>
                    <div>
                      <label className="text-[11px] uppercase tracking-wider text-neutral-500 font-bold">{t.color}</label>
                      <div className="mt-2 flex gap-2 flex-wrap">
                        {['#ffffff', '#000000', '#06b6d4', '#f59e0b', '#ec4899', '#10b981', '#ef4444'].map(c => (
                          <button key={c} onClick={() => setTextColor(c)} className={`w-8 h-8 rounded-full border-2 transition-transform ${textColor === c ? 'border-white scale-110' : 'border-white/20'}`} style={{ background: c }} aria-label={c} />
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="text-[11px] uppercase tracking-wider text-neutral-500 font-bold">{t.size} · {textSize}px</label>
                      <input type="range" min={20} max={120} step={2} value={textSize} onChange={(e) => setTextSize(parseInt(e.target.value))} className="w-full accent-cyan-400 mt-2" />
                    </div>
                    <div>
                      <label className="text-[11px] uppercase tracking-wider text-neutral-500 font-bold">{t.position}</label>
                      <div className="mt-2 grid grid-cols-3 gap-1.5">
                        {[['top', t.top], ['center', t.center], ['bottom', t.bottom]].map(([k, l]) => (
                          <button key={k} onClick={() => setTextPos(k)} className={`h-9 rounded-lg text-[12px] font-bold transition-colors ${textPos === k ? 'bg-white text-black' : 'bg-white/5 text-neutral-300 hover:bg-white/10'}`}>{l}</button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                {tab === 'stickers' && (
                  <div>
                    <label className="text-[11px] uppercase tracking-wider text-neutral-500 font-bold">{t.animation}</label>
                    <div className="mt-2 mb-4 grid grid-cols-5 gap-1">
                      {ANIMS.map(a => (
                        <button key={a.key} onClick={() => setStickerAnim(a.key)} className={`h-9 rounded-lg text-[11px] font-bold transition-colors ${stickerAnim === a.key ? 'bg-white text-black' : 'bg-white/5 text-neutral-300 hover:bg-white/10'}`}>
                          {lang === 'sv' ? a.labelSv : a.labelAr}
                        </button>
                      ))}
                    </div>
                    <p className="text-[11.5px] text-neutral-400 mb-3">{t.drag}</p>
                    <div className="grid grid-cols-6 gap-1.5">
                      {STICKERS.map((em, i) => (
                        <button key={i} onClick={() => addSticker(em)} className="aspect-square rounded-lg bg-white/5 hover:bg-white/15 hover:scale-105 transition-all flex items-center justify-center text-2xl">
                          {em}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {tab === 'filters' && (
                  <div className="space-y-4">
                    <label className="text-[11px] uppercase tracking-wider text-neutral-500 font-bold">{t.chooseFilter}</label>
                    <div className="grid grid-cols-2 gap-2">
                      {FILTERS.map(f => (
                        <button key={f.key} onClick={() => setFilterKey(f.key)} className={`h-16 rounded-xl border-2 flex items-center justify-center relative overflow-hidden transition-all ${filterKey === f.key ? 'border-cyan-400 bg-white/10' : 'border-white/10 bg-white/5 hover:bg-white/10'}`}>
                          <div className="absolute inset-0" style={{ background: filterPreview(f.key), filter: f.css }} />
                          <span className="relative text-[12.5px] font-bold text-white drop-shadow-md">{lang === 'sv' ? f.labelSv : f.labelAr}</span>
                        </button>
                      ))}
                    </div>
                    <div>
                      <label className="text-[11px] uppercase tracking-wider text-neutral-500 font-bold">{t.intensity} · {filterIntensity}%</label>
                      <input type="range" min={0} max={100} step={1} value={filterIntensity} onChange={(e) => setFilterIntensity(parseInt(e.target.value))} className="w-full accent-cyan-400 mt-2" />
                    </div>
                  </div>
                )}
                {tab === 'captions' && (
                  <div className="space-y-4">
                    <div>
                      <label className="text-[11px] uppercase tracking-wider text-neutral-500 font-bold">{lang === 'sv' ? 'Språk' : 'اللغة'}</label>
                      <select value={captionLang} onChange={(e) => setCaptionLang(e.target.value)} className="mt-1.5 w-full h-10 px-3 rounded-xl bg-white/5 border border-white/10 text-white text-[13px] font-semibold focus:outline-none focus:ring-2 focus:ring-cyan-400">
                        <option value="sv-SE">Svenska (SE)</option>
                        <option value="ar-SA">العربية (SA)</option>
                        <option value="en-US">English (US)</option>
                        <option value="en-GB">English (UK)</option>
                        <option value="es-ES">Español</option>
                        <option value="fr-FR">Français</option>
                        <option value="de-DE">Deutsch</option>
                        <option value="it-IT">Italiano</option>
                        <option value="pt-BR">Português (BR)</option>
                        <option value="ru-RU">Русский</option>
                        <option value="tr-TR">Türkçe</option>
                        <option value="ja-JP">日本語</option>
                        <option value="zh-CN">中文</option>
                      </select>
                    </div>
                    <div className="flex items-center gap-2">
                      {!aiListening ? (
                        <button onClick={startAI} className="flex-1 h-11 rounded-xl bg-gradient-to-r from-cyan-500 to-fuchsia-500 text-white font-bold text-[13px] flex items-center justify-center gap-2 hover:opacity-95">
                          <Mic className="w-4 h-4" /> {t.startAI}
                        </button>
                      ) : (
                        <button onClick={stopAI} className="flex-1 h-11 rounded-xl bg-red-500 hover:bg-red-600 text-white font-bold text-[13px] flex items-center justify-center gap-2">
                          <MicOff className="w-4 h-4" /> {t.stopAI}
                        </button>
                      )}
                    </div>
                    {!speechSupported && <p className="text-[11.5px] text-amber-400">{t.aiUnsupported}</p>}
                    <p className="text-[11px] text-neutral-500 leading-relaxed">{t.aiHint}</p>
                    <div className="pt-3 border-t border-white/10">
                      <label className="text-[11px] uppercase tracking-wider text-neutral-500 font-bold">{t.addManual}</label>
                      <div className="mt-2 flex gap-2">
                        <Input value={manualCap} onChange={(e) => setManualCap(e.target.value)} placeholder={t.capPlaceholder} className="flex-1 bg-white/5 border-white/10 text-white h-10 rounded-lg text-[13px] focus-visible:ring-cyan-400 focus-visible:ring-offset-0" onKeyDown={(e) => e.key === 'Enter' && addManualCaption()} />
                        <button onClick={addManualCaption} className="h-10 px-3 rounded-lg bg-white text-black font-bold text-[12px] flex items-center gap-1">
                          <Plus className="w-3.5 h-3.5" /> {fmt(currentTime)}
                        </button>
                      </div>
                    </div>
                    {captions.length > 0 && (
                      <div className="pt-3 border-t border-white/10">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-[11px] uppercase tracking-wider text-neutral-500 font-bold">{captions.length} {lang === 'sv' ? 'rader' : 'سطر'}</span>
                          <button onClick={() => setCaptions([])} className="text-[11px] text-red-400 hover:text-red-300 font-semibold">{t.clearCaps}</button>
                        </div>
                        <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
                          {captions.map((c, i) => (
                            <div key={i} className="group flex items-center gap-2 p-2 rounded-lg bg-white/5 hover:bg-white/10">
                              <span className="text-[10px] font-bold tabular-nums bg-amber-500/20 text-amber-300 rounded px-1.5 py-0.5">{fmt(c.t)}</span>
                              <span className="flex-1 text-[12.5px] truncate">{c.text}</span>
                              <button onClick={() => removeCaption(i)} className="opacity-0 group-hover:opacity-100 w-6 h-6 rounded-full hover:bg-red-500/30 flex items-center justify-center transition-all">
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {tab === 'music' && (
                  <div className="space-y-3">
                    <label className="text-[11px] uppercase tracking-wider text-neutral-500 font-bold">{t.pickTrack}</label>
                    <button onClick={() => setTrackId(null)} className={`w-full flex items-center gap-3 p-3 rounded-xl transition-colors ${!trackId ? 'bg-white/10 border border-cyan-400' : 'bg-white/5 border border-transparent hover:bg-white/10'}`}>
                      <div className="w-10 h-10 rounded-lg bg-neutral-700 flex items-center justify-center flex-shrink-0">
                        <Music2 className="w-4 h-4 text-neutral-300" />
                      </div>
                      <div className="flex-1 text-left rtl:text-right min-w-0">
                        <div className="text-[13px] font-bold truncate">{t.original}</div>
                        <div className="text-[11px] text-neutral-500">{lang === 'sv' ? 'Video-ljud' : 'صوت الفيديو'}</div>
                      </div>
                      {!trackId && <Check className="w-4 h-4 text-cyan-400" />}
                    </button>
                    {availableTracks.length === 0 ? (
                      <div className="p-4 rounded-xl bg-white/5 text-[12px] text-neutral-400 text-center">{t.noTracks}</div>
                    ) : (
                      availableTracks.map(trk => (
                        <button key={trk.id} onClick={() => setTrackId(trk.id)} className={`w-full flex items-center gap-3 p-3 rounded-xl transition-colors ${trackId === trk.id ? 'bg-white/10 border border-cyan-400' : 'bg-white/5 border border-transparent hover:bg-white/10'}`}>
                          <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${trk.color} flex items-center justify-center flex-shrink-0`}>
                            <Music2 className="w-4 h-4 text-white" />
                          </div>
                          <div className="flex-1 text-left rtl:text-right min-w-0">
                            <div className="text-[13px] font-bold truncate">{trk.title}</div>
                            <div className="text-[11px] text-neutral-400 truncate">{trk.artist} · {trk.bpm} BPM</div>
                          </div>
                          {trackId === trk.id && <Check className="w-4 h-4 text-cyan-400" />}
                        </button>
                      ))
                    )}
                    {trackId && (
                      <div className="pt-4 mt-2 border-t border-white/10 space-y-3">
                        <div className="text-[11px] uppercase tracking-wider text-neutral-500 font-bold">{t.musicFade}</div>
                        <p className="text-[11.5px] text-neutral-400">{t.fadeInfo}</p>
                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-[11.5px] font-semibold text-neutral-300">{t.fadeIn}</span>
                            <span className="text-[11px] tabular-nums text-cyan-300 font-bold">{fadeIn.toFixed(1)} {t.sec}</span>
                          </div>
                          <input type="range" min={0} max={3} step={0.1} value={fadeIn} onChange={(e) => setFadeIn(parseFloat(e.target.value))} className="w-full accent-cyan-400" />
                        </div>
                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-[11.5px] font-semibold text-neutral-300">{t.fadeOut}</span>
                            <span className="text-[11px] tabular-nums text-fuchsia-300 font-bold">{fadeOut.toFixed(1)} {t.sec}</span>
                          </div>
                          <input type="range" min={0} max={3} step={0.1} value={fadeOut} onChange={(e) => setFadeOut(parseFloat(e.target.value))} className="w-full accent-fuchsia-400" />
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {tab === 'transition' && (
                  <div className="space-y-3">
                    <label className="text-[11px] uppercase tracking-wider text-neutral-500 font-bold">{t.pickTransition}</label>
                    <div className="grid grid-cols-2 gap-2">
                      {TRANSITIONS.map(tr => (
                        <button key={tr.key} onClick={() => { setTransition(tr.key); if (videoRef.current) { videoRef.current.currentTime = trimStart; } }} className={`h-16 rounded-xl border transition-all flex items-center justify-center relative overflow-hidden ${transition === tr.key ? 'border-cyan-400 bg-white/10' : 'border-white/10 bg-white/5 hover:bg-white/10'}`}>
                          <span className="text-[13px] font-bold">{lang === 'sv' ? tr.labelSv : tr.labelAr}</span>
                        </button>
                      ))}
                    </div>
                    <p className="text-[11.5px] text-neutral-400 leading-relaxed pt-2">{t.transitionInfo}</p>
                  </div>
                )}
                {tab === 'projects' && (
                  <div className="space-y-3">
                    {autoSavedAt && (
                      <div className="flex items-center justify-between p-2.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                          <span className="text-[11px] text-emerald-200 font-semibold truncate">{lang === 'sv' ? 'Autosparat' : 'حفظ تلقائي'} · {new Date(autoSavedAt).toLocaleTimeString(lang === 'sv' ? 'sv-SE' : 'ar-SA', { hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                        <button data-testid="restore-autosave-btn" onClick={restoreAutoSave} className="text-[11px] font-bold text-emerald-300 hover:text-emerald-200 whitespace-nowrap">{lang === 'sv' ? 'Återställ' : 'استعادة'}</button>
                      </div>
                    )}
                    <label className="text-[11px] uppercase tracking-wider text-neutral-500 font-bold">{t.saveProject}</label>
                    <Input value={projectName} onChange={(e) => setProjectName(e.target.value)} placeholder={t.projectNamePh} className="bg-white/5 border-white/10 text-white h-10 rounded-lg text-[13px] focus-visible:ring-cyan-400 focus-visible:ring-offset-0" />
                    <div className="grid grid-cols-2 gap-2">
                      <button onClick={saveProject} disabled={!videoUrl} className="h-10 rounded-lg bg-white/10 hover:bg-white/15 disabled:opacity-40 text-white font-bold text-[12px] flex items-center justify-center gap-1.5 border border-white/10">
                        <Plus className="w-3.5 h-3.5" /> {lang === 'sv' ? 'Lokalt' : 'محلياً'}
                      </button>
                      <button onClick={saveProjectCloud} disabled={!videoUrl} className="h-10 rounded-lg bg-cyan-500 hover:bg-cyan-600 disabled:opacity-40 text-white font-bold text-[12px] flex items-center justify-center gap-1.5">
                        <Cloud className="w-3.5 h-3.5" /> {user ? (lang === 'sv' ? 'Molnet' : 'السحابة') : (lang === 'sv' ? 'Logga in' : 'دخول')}
                      </button>
                    </div>

                    {user && (
                      <div className="pt-3 border-t border-white/10">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-1.5">
                            <Cloud className="w-3 h-3 text-cyan-400" />
                            <span className="text-[11px] uppercase tracking-wider text-neutral-500 font-bold">{lang === 'sv' ? 'Molnprojekt' : 'مشاريع سحابية'}</span>
                          </div>
                          <span className="text-[11px] text-neutral-500">{cloudProjects.length}</span>
                        </div>
                        {cloudProjects.length === 0 ? (
                          <div className="p-4 rounded-xl bg-white/5 text-[11.5px] text-neutral-400 text-center">{lang === 'sv' ? 'Inga molnprojekt ännu' : 'لا توجد مشاريع سحابية'}</div>
                        ) : (
                          <div className="space-y-1.5 max-h-52 overflow-y-auto pr-1">
                            {cloudProjects.map(p => (
                              <div key={p.id} className="group flex items-center gap-2 p-2 rounded-xl bg-cyan-500/5 hover:bg-cyan-500/10 border border-cyan-400/10">
                                <div className="w-11 h-7 rounded-md bg-gradient-to-br from-cyan-500 to-fuchsia-500 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <div className="text-[12px] font-bold text-white truncate">{p.name}</div>
                                  <div className="text-[10px] text-cyan-300/70">{lang === 'sv' ? 'Moln' : 'سحابي'}</div>
                                </div>
                                <button onClick={() => loadCloudProject(p)} className="h-7 px-2 rounded-md bg-white/10 hover:bg-white/20 text-[10.5px] font-bold">{t.loadProj}</button>
                                <button onClick={() => deleteCloudProject(p.id)} className="w-7 h-7 rounded-md hover:bg-red-500/20 text-neutral-400 hover:text-red-300 flex items-center justify-center">
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    <div className="pt-3 border-t border-white/10">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[11px] uppercase tracking-wider text-neutral-500 font-bold">{lang === 'sv' ? 'Lokala projekt' : 'مشاريع محلية'}</span>
                        <span className="text-[11px] text-neutral-500">{savedProjects.length}</span>
                      </div>
                      {savedProjects.length === 0 ? (
                        <div className="p-4 rounded-xl bg-white/5 text-[11.5px] text-neutral-400 text-center">{t.noProjects}</div>
                      ) : (
                        <div className="space-y-1.5 max-h-52 overflow-y-auto pr-1">
                          {savedProjects.map(p => (
                            <div key={p.id} className="group flex items-center gap-2 p-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/5">
                              {p.thumb ? (
                                <img src={p.thumb} alt={p.name} className="w-11 h-7 rounded-md object-cover flex-shrink-0" />
                              ) : (
                                <div className="w-11 h-7 rounded-md bg-gradient-to-br from-cyan-500 to-fuchsia-500 flex-shrink-0" />
                              )}
                              <div className="flex-1 min-w-0">
                                <div className="text-[12px] font-bold text-white truncate">{p.name}</div>
                                <div className="text-[10px] text-neutral-500">{new Date(p.savedAt).toLocaleDateString(lang === 'sv' ? 'sv-SE' : 'ar-SA')}</div>
                              </div>
                              <button onClick={() => loadProject(p)} className="h-7 px-2 rounded-md bg-white/10 hover:bg-white/20 text-[10.5px] font-bold">{t.loadProj}</button>
                              <button onClick={() => deleteProject(p.id)} className="w-7 h-7 rounded-md hover:bg-red-500/20 text-neutral-400 hover:text-red-300 flex items-center justify-center">
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {tab === 'share' && (
                  <div className="space-y-3">
                    <label className="text-[11px] uppercase tracking-wider text-neutral-500 font-bold">{lang === 'sv' ? 'Hashtaggar' : 'الوسوم'}</label>
                    <Input value={tagsInput} onChange={(e) => setTagsInput(e.target.value)} placeholder={lang === 'sv' ? 'sommar, dance, viral' : 'صيف، رقص، رائج'} className="bg-white/5 border-white/10 text-white h-10 rounded-lg text-[13px]" />
                    <p className="text-[11px] text-neutral-500">{lang === 'sv' ? 'Separera med komma. Max 10 taggar. Hjälper folk hitta din video.' : 'افصل بفواصل. الحد الأقصى 10 وسوم.'}</p>
                    <div className="pt-3 border-t border-white/10 space-y-3">
                    <label className="text-[11px] uppercase tracking-wider text-neutral-500 font-bold">{t.shareLink}</label>
                    <p className="text-[11.5px] text-neutral-400 leading-relaxed">{t.shareInfo}</p>
                    <Button onClick={buildShareLink} className="w-full h-11 rounded-xl bg-white/10 hover:bg-white/15 text-white font-bold text-[13px] border border-white/10">
                      <Share2 className="w-4 h-4 mr-2 rtl:mr-0 rtl:ml-2" /> {lang === 'sv' ? 'Skapa länk' : 'أنشئ الرابط'}
                    </Button>
                    {shareUrl && (
                      <div className="space-y-2">
                        <div className="p-3 rounded-xl bg-black/40 border border-white/10 text-[11px] text-cyan-300 break-all font-mono">{shareUrl}</div>
                        <Button onClick={copyShareLink} className="w-full h-10 rounded-xl bg-cyan-500 hover:bg-cyan-600 text-white font-bold text-[12.5px]">
                          <Copy className="w-3.5 h-3.5 mr-2 rtl:mr-0 rtl:ml-2" /> {t.copyLink}
                        </Button>
                      </div>
                    )}
                    </div>
                  </div>
                )}
              </div>
              <div className="p-4 border-t border-white/10 space-y-2 flex-shrink-0">
                {cloudUrl && (
                  <div className="p-3 rounded-xl bg-cyan-500/10 border border-cyan-400/30 space-y-2">
                    <div className="flex items-center gap-2 text-cyan-300">
                      <Cloud className="w-3.5 h-3.5" />
                      <span className="text-[11px] font-bold uppercase tracking-wider">{lang === 'sv' ? 'Delbar länk' : 'رابط قابل للمشاركة'}</span>
                    </div>
                    <div className="text-[10.5px] text-cyan-200 break-all font-mono">{cloudUrl}</div>
                    <button onClick={async () => { try { await navigator.clipboard.writeText(cloudUrl); toast({ title: lang === 'sv' ? 'Kopierat!' : 'تم النسخ!' }); } catch(_){} }} className="w-full h-8 rounded-lg bg-cyan-500 hover:bg-cyan-600 text-white font-bold text-[11.5px] flex items-center justify-center gap-1.5">
                      <Copy className="w-3 h-3" /> {lang === 'sv' ? 'Kopiera länk' : 'انسخ الرابط'}
                    </button>
                    <div className="pt-2 border-t border-cyan-400/20">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-cyan-200/70 mb-1.5">{lang === 'sv' ? 'Dela direkt' : 'شارك مباشرة'}</div>
                      <div className="grid grid-cols-4 gap-1.5">
                        <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(lang==='sv'?'Kolla min video på CapCute!':'شاهد فيديوهي على CapCute!')}&url=${encodeURIComponent(cloudUrl)}`} target="_blank" rel="noreferrer" className="h-9 rounded-lg bg-black hover:bg-neutral-800 flex items-center justify-center text-white transition-colors" title="X">
                          <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                        </a>
                        <a href={`https://wa.me/?text=${encodeURIComponent(cloudUrl)}`} target="_blank" rel="noreferrer" className="h-9 rounded-lg bg-emerald-500 hover:bg-emerald-600 flex items-center justify-center text-white transition-colors" title="WhatsApp">
                          <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M20.52 3.48A11.9 11.9 0 0 0 12.06 0C5.5 0 .16 5.34.16 11.9c0 2.1.55 4.14 1.6 5.94L0 24l6.32-1.66a11.87 11.87 0 0 0 5.74 1.46h.01c6.55 0 11.89-5.34 11.89-11.9 0-3.18-1.24-6.17-3.44-8.42zM12.07 21.8h-.01a9.9 9.9 0 0 1-5.04-1.38l-.36-.22-3.75.98 1-3.66-.24-.38a9.86 9.86 0 0 1-1.53-5.24c0-5.46 4.44-9.9 9.9-9.9 2.65 0 5.13 1.03 7 2.9a9.83 9.83 0 0 1 2.9 7c0 5.46-4.44 9.9-9.87 9.9zm5.42-7.42c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15s-.77.97-.94 1.17c-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.88-.79-1.48-1.76-1.65-2.06-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51-.17-.01-.37-.01-.57-.01-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48s1.06 2.88 1.21 3.08c.15.2 2.1 3.2 5.08 4.49.71.31 1.26.49 1.69.63.71.23 1.36.2 1.87.12.57-.09 1.76-.72 2-1.42.24-.7.24-1.29.17-1.42-.07-.13-.27-.2-.57-.35z"/></svg>
                        </a>
                        <button onClick={async () => { try { await navigator.clipboard.writeText(cloudUrl); toast({ title: lang === 'sv' ? 'Länk kopierad - klistra i TikTok' : 'تم النسخ - الصقه في تيك توك' }); } catch(_){} }} className="h-9 rounded-lg bg-neutral-900 hover:bg-neutral-800 flex items-center justify-center text-white transition-colors" title="TikTok">
                          <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64c.298-.006.596.028.882.11V9.4a6.33 6.33 0 0 0-1-.05A6.34 6.34 0 0 0 5.82 20.1a6.34 6.34 0 0 0 10.86-4.43V8.68a8.16 8.16 0 0 0 4.77 1.52V6.79a4.85 4.85 0 0 1-1.86-.1z"/></svg>
                        </button>
                        <button onClick={async () => { try { await navigator.clipboard.writeText(cloudUrl); toast({ title: lang === 'sv' ? 'Länk kopierad - klistra i Instagram' : 'تم النسخ - الصقه في إنستغرام' }); } catch(_){} }} className="h-9 rounded-lg bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 hover:opacity-90 flex items-center justify-center text-white transition-opacity" title="Instagram">
                          <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M12 2.16c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.42.36 1.06.41 2.23.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.42.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.23-.41a3.72 3.72 0 0 1-1.38-.9 3.72 3.72 0 0 1-.9-1.38c-.16-.42-.36-1.06-.41-2.23C2.17 15.58 2.16 15.2 2.16 12s.01-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.16 1.06-.36 2.23-.41C8.42 2.17 8.8 2.16 12 2.16zM12 0C8.74 0 8.33.01 7.05.07 5.78.13 4.9.33 4.14.63a5.88 5.88 0 0 0-2.13 1.38A5.88 5.88 0 0 0 .63 4.14C.33 4.9.13 5.78.07 7.05.01 8.33 0 8.74 0 12s.01 3.67.07 4.95c.06 1.27.26 2.15.56 2.91.31.79.72 1.46 1.38 2.13a5.88 5.88 0 0 0 2.13 1.38c.76.3 1.64.5 2.91.56C8.33 23.99 8.74 24 12 24s3.67-.01 4.95-.07c1.27-.06 2.15-.26 2.91-.56a5.88 5.88 0 0 0 2.13-1.38 5.88 5.88 0 0 0 1.38-2.13c.3-.76.5-1.64.56-2.91.06-1.28.07-1.69.07-4.95s-.01-3.67-.07-4.95c-.06-1.27-.26-2.15-.56-2.91a5.88 5.88 0 0 0-1.38-2.13A5.88 5.88 0 0 0 19.86.63C19.1.33 18.22.13 16.95.07 15.67.01 15.26 0 12 0zm0 5.84a6.16 6.16 0 1 0 0 12.32 6.16 6.16 0 0 0 0-12.32zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.4-11.85a1.44 1.44 0 1 0 0 2.88 1.44 1.44 0 0 0 0-2.88z"/></svg>
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                <Button onClick={exportVideo} disabled={exporting} className="w-full h-12 rounded-full bg-white text-black hover:bg-neutral-200 font-bold text-[14px]">
                  <Download className="w-4 h-4 mr-2 rtl:mr-0 rtl:ml-2" />
                  {exporting ? t.exporting : t.export}
                </Button>
                <button onClick={clearVideo} className="w-full h-10 rounded-full bg-white/5 hover:bg-red-500/20 hover:text-red-300 text-neutral-400 text-[12.5px] font-semibold flex items-center justify-center gap-1.5 transition-colors">
                  <Trash2 className="w-3.5 h-3.5" /> {t.clear}
                </button>
              </div>
            </aside>
          </div>
        )}
      </div>
    </div>
  );
};

const filterPreview = (key) => {
  const map = {
    none: 'linear-gradient(135deg,#525252,#262626)',
    vintage: 'linear-gradient(135deg,#c2996a,#7a4f2b)',
    cinematic: 'linear-gradient(135deg,#1e293b,#0f172a)',
    cold: 'linear-gradient(135deg,#3b82f6,#1e40af)',
    warm: 'linear-gradient(135deg,#f97316,#c2410c)',
    bw: 'linear-gradient(135deg,#a3a3a3,#404040)',
    dream: 'linear-gradient(135deg,#f0abfc,#d946ef)'
  };
  return map[key] || map.none;
};

export default VideoEditor;
